import React, {useMemo, useState} from "react";
import Box from "@mui/material/Box";
import {Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {withCompositionIdQuery} from "../../../utils/withCompositionIdQuery";
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner,
} from "material-react-table";
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import ButtonCreate from "../../../components/UI/button/ButtonCreate";
import ModalDependentModuleComposition from "../../../components/UI/modal/ModalDependentModuleComposition";
import CompositionController from "../../../API/organization/CompositionController";
import {replaceEmptyStringsOnNull} from "../../../utils/ReplaceEmptyStringsOnNull";
import OverflowTooltip from "../../../components/UI/tooltip/OverflowTooltip";

const DIMENSION_COL_WIDTH = 200
const NAME_COL_WIDTH = 400
const DIMENSION_COL_IDS = new Set(['patternW', 'patternH', 'patternD'])
const TABLE_WIDTH = NAME_COL_WIDTH + DIMENSION_COL_WIDTH * 3

const dimensionColSx = {
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    maxWidth: DIMENSION_COL_WIDTH,
    flex: `0 0 ${DIMENSION_COL_WIDTH}px`,
    boxSizing: 'border-box',
}

const nameColSx = {
    width: NAME_COL_WIDTH,
    minWidth: NAME_COL_WIDTH,
    maxWidth: NAME_COL_WIDTH,
    flex: `0 0 ${NAME_COL_WIDTH}px`,
    boxSizing: 'border-box',
    overflow: 'hidden',
}

const columnSx = (columnId) => (DIMENSION_COL_IDS.has(columnId) ? dimensionColSx : nameColSx)

const DependentModuleCompositionsTable = ({
                                              data,
                                              setData,
                                              compositionId,
                                              compositionName,
                                              moduleCompositionId,
                                              handleModalAlert,
                                          }) => {
    const rows = useMemo(
        () => (Array.isArray(data) ? data : []),
        [data],
    );
    const [modal, setModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const canAdd = moduleCompositionId != null && moduleCompositionId !== ""

    const columns = useMemo(() => [
        {
            id: 'name',
            header: 'Наименование',
            accessorKey: 'name',
            grow: false,
            size: NAME_COL_WIDTH,
            minSize: NAME_COL_WIDTH,
            maxSize: NAME_COL_WIDTH,
            Cell: ({row, renderedCellValue}) => (
                <OverflowTooltip title={row.original.name}>
                    <Link to={withCompositionIdQuery(
                        '/organization/dependent-module-composition/' + row.original.id,
                        compositionId,
                        compositionName,
                    )}>
                        {renderedCellValue}
                    </Link>
                </OverflowTooltip>
            ),
        },
        {
            id: 'patternW',
            header: 'Ширина',
            accessorKey: 'patternW',
            size: DIMENSION_COL_WIDTH,
            minSize: DIMENSION_COL_WIDTH,
            maxSize: DIMENSION_COL_WIDTH,
            grow: false,
        },
        {
            id: 'patternH',
            header: 'Высота',
            accessorKey: 'patternH',
            size: DIMENSION_COL_WIDTH,
            minSize: DIMENSION_COL_WIDTH,
            maxSize: DIMENSION_COL_WIDTH,
            grow: false,
        },
        {
            id: 'patternD',
            header: 'Глубина',
            accessorKey: 'patternD',
            size: DIMENSION_COL_WIDTH,
            minSize: DIMENSION_COL_WIDTH,
            maxSize: DIMENSION_COL_WIDTH,
            grow: false,
        },
    ], [compositionId, compositionName]);

    const handleClose = () => {
        setModal(false)
    }

    const handleSubmit = (values, close) => {
        if (!canAdd) {
            return
        }
        setLoading(true)
        const payload = {
            ...replaceEmptyStringsOnNull({
                name: values.name,
                patternW: values.patternW,
                patternH: values.patternH,
                patternD: values.patternD,
            }),
            moduleCompositions: [],
        }
        CompositionController.createDependentModuleComposition(moduleCompositionId, payload)
            .then(function (response) {
                const row = response?.data
                if (row?.id != null && setData) {
                    setData((prev) => [...(Array.isArray(prev) ? prev : rows), row])
                }
                close()
                handleModalAlert?.("Данные добавлены", "success")
            })
            .catch(function (error) {
                handleModalAlert?.(error)
            })
            .finally(() => setLoading(false))
    }

    return (
        <Box sx={{overflow: 'auto', width: '100%'}}>
            <Box sx={{width: TABLE_WIDTH}}>
            <Box sx={{
                display: 'flex',
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "16px",
            }}>
                <Typography sx={{fontSize: "16px", fontWeight: 700}}>
                    Связанные модули
                </Typography>
                {canAdd && <ButtonCreate
                    text={"Добавить"}
                    width={140}
                    onClick={() => setModal(true)}
                />}
            </Box>
            <MaterialReactTable
                renderTopToolbar={({table}) => (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "7px",
                        backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0
                            ? theme.palette.primary.light
                            : "transparent",
                    }}>
                        <MRT_ToolbarAlertBanner table={table}/>
                        <Box sx={{display: "flex"}}>
                            <MRT_ToggleFiltersButton table={table}/>
                            <MRT_GlobalFilterTextField table={table}/>
                        </Box>
                    </Box>
                )}
                muiTablePaperProps={{
                    sx: {
                        width: 'fit-content',
                        maxWidth: 'fit-content',
                    },
                }}
                muiTableContainerProps={{
                    sx: {
                        width: TABLE_WIDTH,
                        maxWidth: TABLE_WIDTH,
                        maxHeight: `calc(100vh - 420px)`,
                    },
                }}
                muiTableProps={{
                    sx: {
                        width: TABLE_WIDTH,
                        tableLayout: 'fixed',
                    },
                }}
                muiTableBodyCellProps={({column}) => ({
                    sx: {
                        p: '0px 0px 0px 10px',
                        m: 0,
                        border: "none",
                        "&:after": {display: "none"},
                        "&:not(:last-of-type)": {borderRight: '1px solid #EAEBEA'},
                        "& a": {
                            color: "#006CCC",
                            textDecoration: "none",
                            "&:hover": {textDecoration: "underline"},
                        },
                        ...columnSx(column.id),
                    },
                })}
                muiTableHeadCellProps={({column}) => ({
                    sx: {
                        border: '1px solid #EAEBEA',
                        backgroundColor: "#F5F6FA",
                        padding: "10px",
                        paddingBottom: "0.2rem",
                        paddingTop: "0.2rem",
                        ...columnSx(column.id),
                    },
                })}
                muiTableBodyRowProps={() => ({
                    sx: (theme) => ({
                        height: '36px',
                        backgroundColor: "white",
                        "&:hover": {backgroundColor: theme.palette.primary.stroke},
                        '&:nth-of-type(even)': {
                            backgroundColor: theme.palette.primary.lightGrey,
                            "&:hover": {backgroundColor: theme.palette.primary.stroke},
                        },
                    }),
                })}
                columns={columns}
                data={rows}
                getRowId={(originalRow) => originalRow.id}
                enableToolbarInternalActions={false}
                initialState={{
                    showGlobalFilter: true,
                    density: "compact",
                    pagination: {pageIndex: 0, pageSize: 100},
                }}
                localization={MRT_Localization_RU}
            />
            </Box>
            {modal && canAdd && <ModalDependentModuleComposition
                title={"Добавить"}
                open={modal}
                onClose={handleClose}
                handleSubmit={handleSubmit}
                loading={loading}
            />}
        </Box>
    );
};

export default DependentModuleCompositionsTable;
